import { produce } from 'immer';
import _ from 'lodash';

import { RESET_STATE } from 'store/global/actions';

import { CHECKLISTS_ACTION_TYPE, LOAN_APPLICATION_ACTION_TYPE } from '../actions';

const initialState = {
  currentChecklistId: null,
};

const isChecklistKey = key => !(key in initialState);

const getItemResultsById = lane => _.mapValues(_.keyBy(lane?.items, 'checklistItemId'), 'result');

const getIsSavedLaneOutdated = (savedLane, pendingChangesLane) =>
  !_.isEmpty(savedLane?.items) &&
  !_.isEmpty(pendingChangesLane?.items) &&
  !_.isEqual(getItemResultsById(savedLane), getItemResultsById(pendingChangesLane));

const OUTDATED_SAVED_LANE_KEYS = ['isSavedLaneOutdated', 'outdatedSavedLaneResults'];

const markSavedLaneOutdated = entity => {
  const base = _.omit(entity, OUTDATED_SAVED_LANE_KEYS);

  if (!entity.withPendingChanges || !getIsSavedLaneOutdated(entity, entity.withPendingChanges)) {
    return base;
  }

  return {
    ...base,
    isSavedLaneOutdated: true,
    outdatedSavedLaneResults: getItemResultsById(entity),
  };
};

const reconcileSavedLane = (incoming, previous) => {
  if (!previous?.isSavedLaneOutdated) {
    return incoming;
  }

  const withPendingChanges = incoming.withPendingChanges ?? previous.withPendingChanges;
  const isSavedLaneRegenerated = !_.isEqual(
    getItemResultsById(incoming),
    previous.outdatedSavedLaneResults,
  );

  return !isSavedLaneRegenerated && getIsSavedLaneOutdated(incoming, withPendingChanges)
    ? {
        ...incoming,
        withPendingChanges,
        isSavedLaneOutdated: true,
        outdatedSavedLaneResults: previous.outdatedSavedLaneResults,
      }
    : incoming;
};

const mapChecklistEntities = (entities, mapEntity) =>
  _.mapValues(entities, (entity, key) => (isChecklistKey(key) ? mapEntity(entity, key) : entity));

const checklistsReducer = (state = initialState, { type, payload }) =>
  produce(state, () => {
    switch (type) {
      case CHECKLISTS_ACTION_TYPE.GET_CHECKLISTS:
        return payload
          ? mapChecklistEntities(payload, (entity, key) => reconcileSavedLane(entity, state[key]))
          : initialState;

      case CHECKLISTS_ACTION_TYPE.GET_CHECKLIST: {
        const { requestedChecklistIds = [], checklists = {} } = payload || {};
        const removedChecklistIds = requestedChecklistIds.filter(checklistId =>
          _.isNil(checklists[checklistId]),
        );
        const reconciledChecklists = _.mapValues(checklists, (entity, key) =>
          reconcileSavedLane(entity, state[key]),
        );

        // Entities and control keys share this flat slice and a requested id comes from a URL segment,
        // so removal has to address own keys only: `_.omit` would path-cast an unmatched dotted segment
        // and strip a nested field off an unrelated cached entity, and a segment colliding with a
        // control key would delete it.
        return _.pickBy(
          { ...state, ...reconciledChecklists },
          (checklistEntity, key) => key in initialState || !removedChecklistIds.includes(key),
        );
      }

      case CHECKLISTS_ACTION_TYPE.SET_CURRENT_CHECKLIST_ID:
        return { ...state, currentChecklistId: payload };

      case LOAN_APPLICATION_ACTION_TYPE.SAVE_LOAN_APPLICATION_CHANGES:
        return mapChecklistEntities(state, markSavedLaneOutdated);

      case LOAN_APPLICATION_ACTION_TYPE.CANCEL_LOAN_CHANGES:
        return mapChecklistEntities(state, entity => _.omit(entity, OUTDATED_SAVED_LANE_KEYS));

      case RESET_STATE:
        return initialState;

      default:
        return state;
    }
  });

export default checklistsReducer;
